import {
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import {
  pickImo,
  pickVesselName,
  normalizeVesselList,
} from 'src/app/modules/PC_modules/onboarding-vessels/onboarding-vessel-mapper.util';
import { PortRequestFormServiceService } from 'src/app/shared/service/port-request-FormService/port-request-form.service.service';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';

export type OverspendStatus = 'Pending' | 'Approved' | 'Rejected' | 'Reviewed';

export interface VotVesselOption {
  vessel_name: string;
  vessel_imo: string;
}

export interface ConsumptionExceptionRecord {
  id: string;
  vesselName: string;
  imoNumber: string;
  fileName: string;
  uploadDate: string;
  uploadedBy: string;
  fileUrl: string;
  mimeType: string;
  status: OverspendStatus;
  fileSizeLabel: string;
  overspendMt: number;
}

@Component({
  selector: 'app-consumption-exceptions',
  templateUrl: './consumption-exceptions.component.html',
  styleUrls: ['./consumption-exceptions.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ConsumptionExceptionsComponent implements OnInit, OnDestroy {
  private static readonly SAMPLE_PDF =
    'assets/sample/consumption-exception-sample.pdf';
  private static readonly MAX_FILE_BYTES = 20 * 1024 * 1024;

  recordForm!: FormGroup;
  filterForm!: FormGroup;
  selectedFile: File | null = null;
  uploadProgress = 0;
  isUploading = false;
  isDragOver = false;
  isFilterOpen = false;
  globalSearch = '';
  tableSearch = '';
  selectedVessels: string[] = [];
  selectedStatuses: string[] = [];
  selectedUploadedBy: string[] = [];
  appliedFromDate = '';
  appliedToDate = '';
  appliedVessels: string[] = [];
  appliedStatuses: string[] = [];
  appliedUploadedBy: string[] = [];
  sortKey: 'uploadDate' | 'vesselName' | 'imoNumber' = 'uploadDate';
  sortDir: 'asc' | 'desc' = 'desc';
  pageIndex = 0;
  pageSize = 5;
  actionMenuId: string | null = null;
  vesselOptions: VotVesselOption[] = [];
  filteredVesselSuggestions: VotVesselOption[] = [];
  isLoadingVessels = false;

  readonly categories = ['Port Stay', 'Maneuvering', 'Cargo Ops', 'Idle', 'Other'];
  readonly priorities = ['Low', 'Medium', 'High', 'Critical'];
  readonly statusFilterOptions: OverspendStatus[] = [
    'Pending',
    'Approved',
    'Rejected',
    'Reviewed',
  ];

  readonly sparkTotalVessels = '4,10,8,14,12,18,16,22,20,24';
  readonly sparkOverspend = '40,55,48,70,62,85,78,95,88,100';
  readonly sparkAverage = '30,42,38,50,45,58,52,60,55,62';
  readonly sparkDocuments = '3,6,5,9,8,12,11,16,18,24';

  private objectUrls: string[] = [];
  private uploadTimer: ReturnType<typeof setInterval> | null = null;

  records: ConsumptionExceptionRecord[] = [
    {
      id: 'demo-1',
      vesselName: 'Ocean Star',
      imoNumber: '9384721',
      fileName: 'Ocean_Star_Report.pdf',
      uploadDate: '2026-07-12',
      uploadedBy: 'Meraki Ops',
      fileUrl: ConsumptionExceptionsComponent.SAMPLE_PDF,
      mimeType: 'application/pdf',
      status: 'Reviewed',
      fileSizeLabel: '2.4 MB',
      overspendMt: 62.4,
    },
    {
      id: 'demo-2',
      vesselName: 'Pacific Horizon',
      imoNumber: '9612045',
      fileName: 'pacific-horizon-exception.pdf',
      uploadDate: '2026-07-10',
      uploadedBy: 'Meraki Admin',
      fileUrl: ConsumptionExceptionsComponent.SAMPLE_PDF,
      mimeType: 'application/pdf',
      status: 'Approved',
      fileSizeLabel: '1.8 MB',
      overspendMt: 48.2,
    },
    {
      id: 'demo-3',
      vesselName: 'Baltic Star',
      imoNumber: '9328810',
      fileName: 'baltic-star-port-variance.docx',
      uploadDate: '2026-07-08',
      uploadedBy: 'Meraki Ops',
      fileUrl: ConsumptionExceptionsComponent.SAMPLE_PDF,
      mimeType:
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      status: 'Pending',
      fileSizeLabel: '890 KB',
      overspendMt: 55.1,
    },
    {
      id: 'demo-4',
      vesselName: 'MT Derin',
      imoNumber: '9483721',
      fileName: 'derin-over-consumption-report.pdf',
      uploadDate: '2026-07-06',
      uploadedBy: 'Meraki Ops',
      fileUrl: ConsumptionExceptionsComponent.SAMPLE_PDF,
      mimeType: 'application/pdf',
      status: 'Rejected',
      fileSizeLabel: '3.1 MB',
      overspendMt: 71.5,
    },
    {
      id: 'demo-5',
      vesselName: 'Atlantic Pride',
      imoNumber: '9456102',
      fileName: 'atlantic-pride-overspend.pdf',
      uploadDate: '2026-07-04',
      uploadedBy: 'Meraki Admin',
      fileUrl: ConsumptionExceptionsComponent.SAMPLE_PDF,
      mimeType: 'application/pdf',
      status: 'Approved',
      fileSizeLabel: '2.0 MB',
      overspendMt: 39.8,
    },
    {
      id: 'demo-6',
      vesselName: 'Nordic Wave',
      imoNumber: '9723451',
      fileName: 'nordic-wave-analysis.pdf',
      uploadDate: '2026-07-01',
      uploadedBy: 'Meraki Ops',
      fileUrl: ConsumptionExceptionsComponent.SAMPLE_PDF,
      mimeType: 'application/pdf',
      status: 'Reviewed',
      fileSizeLabel: '1.2 MB',
      overspendMt: 44.6,
    },
  ];

  previewOpen = true;
  previewTitle = '';
  previewFileName = '';
  previewFileSize = '';
  previewRawUrl = '';
  previewSafeUrl: SafeResourceUrl | null = null;
  previewIsPdf = true;
  previewFullscreen = false;
  zoomLevel = 100;

  readonly acceptedExtensions = ['.pdf', '.doc', '.docx'];

  constructor(
    private requestFormService: RequestFormService,
    private portRequestFormService: PortRequestFormServiceService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer,
    private fb: FormBuilder,
  ) {
    this.filterForm = this.fb.group({
      from_date: [''],
      to_date: [''],
    });
    this.recordForm = this.fb.group({
      vesselName: [''],
      imoNumber: [''],
      category: [''],
      priority: ['Medium'],
    });
    const first = this.records[0];
    if (first) {
      this.openPreview(first, false);
    }
  }

  ngOnInit(): void {
    this.loadVesselOptions();
  }

  @HostBinding('class.pc-light') get pcLight(): boolean {
    return !this.isDarkMode();
  }

  isDarkMode(): boolean {
    return this.requestFormService.isDarkMode();
  }

  get vesselFilterOptions(): string[] {
    return Array.from(new Set(this.records.map((r) => r.vesselName))).sort();
  }

  get uploadedByOptions(): string[] {
    return Array.from(new Set(this.records.map((r) => r.uploadedBy))).sort();
  }

  get hasActiveFilters(): boolean {
    return (
      !!this.appliedFromDate ||
      !!this.appliedToDate ||
      this.appliedVessels.length > 0 ||
      this.appliedStatuses.length > 0 ||
      this.appliedUploadedBy.length > 0
    );
  }

  get showNoVesselSuggestions(): boolean {
    const term = String(this.recordForm.get('vesselName')?.value || '').trim();
    return (
      !!term &&
      !this.isLoadingVessels &&
      this.filteredVesselSuggestions.length === 0
    );
  }

  get totalVessels(): number {
    return new Set(this.records.map((r) => r.imoNumber)).size;
  }

  get totalOverspend(): number {
    return this.records.reduce((sum, r) => sum + (r.overspendMt || 0), 0);
  }

  get avgOverspend(): number {
    if (!this.records.length) {
      return 0;
    }
    return this.totalOverspend / this.records.length;
  }

  get totalDocuments(): number {
    return this.records.length;
  }

  get filteredRecords(): ConsumptionExceptionRecord[] {
    const global = this.globalSearch.trim().toLowerCase();
    const table = this.tableSearch.trim().toLowerCase();
    const query = table || global;

    let rows = [...this.records];

    if (query) {
      rows = rows.filter(
        (r) =>
          r.vesselName.toLowerCase().includes(query) ||
          r.imoNumber.includes(query) ||
          r.fileName.toLowerCase().includes(query) ||
          r.uploadedBy.toLowerCase().includes(query),
      );
    }

    if (this.appliedVessels.length) {
      rows = rows.filter((r) => this.appliedVessels.includes(r.vesselName));
    }

    if (this.appliedStatuses.length) {
      rows = rows.filter((r) => this.appliedStatuses.includes(r.status));
    }

    if (this.appliedUploadedBy.length) {
      rows = rows.filter((r) => this.appliedUploadedBy.includes(r.uploadedBy));
    }

    if (this.appliedFromDate || this.appliedToDate) {
      rows = rows.filter((r) => {
        if (this.appliedFromDate && r.uploadDate < this.appliedFromDate) {
          return false;
        }
        if (this.appliedToDate && r.uploadDate > this.appliedToDate) {
          return false;
        }
        return true;
      });
    }

    rows.sort((a, b) => {
      const av = a[this.sortKey];
      const bv = b[this.sortKey];
      if (av < bv) {
        return this.sortDir === 'asc' ? -1 : 1;
      }
      if (av > bv) {
        return this.sortDir === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return rows;
  }

  get pagedRecords(): ConsumptionExceptionRecord[] {
    const start = this.pageIndex * this.pageSize;
    return this.filteredRecords.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredRecords.length / this.pageSize));
  }

  openFilterDrawer(): void {
    this.isFilterOpen = true;
  }

  closeFilterDrawer(): void {
    this.isFilterOpen = false;
  }

  filterClick(): void {
    this.appliedFromDate = this.toIsoDate(this.filterForm.get('from_date')?.value);
    this.appliedToDate = this.toIsoDate(this.filterForm.get('to_date')?.value);
    this.appliedVessels = [...this.selectedVessels];
    this.appliedStatuses = [...this.selectedStatuses];
    this.appliedUploadedBy = [...this.selectedUploadedBy];
    this.pageIndex = 0;
    this.closeFilterDrawer();
  }

  clearClick(): void {
    this.filterForm.reset({ from_date: '', to_date: '' });
    this.selectedVessels = [];
    this.selectedStatuses = [];
    this.selectedUploadedBy = [];
    this.appliedFromDate = '';
    this.appliedToDate = '';
    this.appliedVessels = [];
    this.appliedStatuses = [];
    this.appliedUploadedBy = [];
    this.pageIndex = 0;
  }

  private toIsoDate(value: unknown): string {
    if (!value) {
      return '';
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString().slice(0, 10);
    }
    const raw = String(value).trim();
    if (!raw) {
      return '';
    }
    // Already YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      return raw.slice(0, 10);
    }
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().slice(0, 10);
    }
    return '';
  }

  get pageStart(): number {
    if (!this.filteredRecords.length) {
      return 0;
    }
    return this.pageIndex * this.pageSize + 1;
  }

  get pageEnd(): number {
    return Math.min(
      (this.pageIndex + 1) * this.pageSize,
      this.filteredRecords.length,
    );
  }

  onVesselNameFocus(): void {
    const term = String(this.recordForm.get('vesselName')?.value || '').trim();
    this.filteredVesselSuggestions = term
      ? this.filterVesselSuggestions(term)
      : this.vesselOptions.slice(0, 25);
  }

  onVesselNameInput(event: Event): void {
    const term = String((event.target as HTMLInputElement).value || '').trim();
    if (!term) {
      this.filteredVesselSuggestions = [];
      this.recordForm.patchValue({ imoNumber: '' }, { emitEvent: false });
      return;
    }

    const exactMatch = this.vesselOptions.find((v) => v.vessel_name === term);
    if (!exactMatch) {
      this.recordForm.patchValue({ imoNumber: '' }, { emitEvent: false });
    }

    this.filteredVesselSuggestions = this.filterVesselSuggestions(term);
  }

  onVesselSelected(event: MatAutocompleteSelectedEvent): void {
    const vesselName = String(event.option.value || '').trim();
    if (!vesselName) {
      this.recordForm.patchValue({ vesselName: '', imoNumber: '' });
      this.filteredVesselSuggestions = [];
      return;
    }

    const vessel = this.vesselOptions.find((v) => v.vessel_name === vesselName);
    this.recordForm.patchValue({
      vesselName,
      imoNumber: vessel?.vessel_imo ?? '',
    });
    this.filteredVesselSuggestions = [];
  }

  private filterVesselSuggestions(term: string): VotVesselOption[] {
    const normalized = term.trim().toLowerCase();
    const imoQuery = normalized.replace(/\D/g, '');

    return this.vesselOptions
      .filter((v) => {
        const nameMatch = v.vessel_name.toLowerCase().includes(normalized);
        const imoMatch =
          !!imoQuery &&
          v.vessel_imo.replace(/\D/g, '').includes(imoQuery);
        return nameMatch || imoMatch;
      })
      .slice(0, 25);
  }

  loadVesselOptions(): void {
    this.isLoadingVessels = true;
    this.recordForm.get('vesselName')?.disable();
    this.portRequestFormService.getOnboardingVesselsData().subscribe({
      next: (data) => {
        const rows = normalizeVesselList(data);
        const byImo = new Map<string, VotVesselOption>();

        rows.forEach((row) => {
          const vessel_name = pickVesselName(row);
          const vessel_imo = pickImo(row);
          if (vessel_name === '—' || vessel_imo === '—') {
            return;
          }
          const imoKey = vessel_imo.replace(/\D/g, '');
          if (!imoKey) {
            return;
          }
          if (!byImo.has(imoKey)) {
            byImo.set(imoKey, { vessel_name, vessel_imo });
          }
        });

        this.vesselOptions = Array.from(byImo.values()).sort((a, b) =>
          a.vessel_name.localeCompare(b.vessel_name),
        );
        this.isLoadingVessels = false;
        this.recordForm.get('vesselName')?.enable();
      },
      error: () => {
        this.vesselOptions = [];
        this.isLoadingVessels = false;
        this.recordForm.get('vesselName')?.enable();
        this.toastr.error('Failed to load vessel list.');
      },
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.handleIncomingFile(file, input);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const file = event.dataTransfer?.files?.[0] ?? null;
    this.handleIncomingFile(file);
  }

  clearSelectedFile(fileInput?: HTMLInputElement): void {
    this.selectedFile = null;
    this.uploadProgress = 0;
    this.isUploading = false;
    if (this.uploadTimer) {
      clearInterval(this.uploadTimer);
      this.uploadTimer = null;
    }
    if (fileInput) {
      fileInput.value = '';
    }
  }

  submitRecord(fileInput?: HTMLInputElement): void {
    const vesselName = String(this.recordForm.get('vesselName')?.value || '').trim();
    const imoNumber = String(this.recordForm.get('imoNumber')?.value || '').trim();

    if (!vesselName) {
      this.toastr.warning('Please enter Vessel Name.');
      return;
    }
    if (!imoNumber) {
      this.toastr.warning('Please enter IMO Number.');
      return;
    }
    if (!/^\d{7}$/.test(imoNumber)) {
      this.toastr.warning('IMO Number must be 7 digits.');
      return;
    }
    if (!this.selectedFile) {
      this.toastr.warning('Please upload a supporting document.');
      return;
    }

    const objectUrl = URL.createObjectURL(this.selectedFile);
    this.objectUrls.push(objectUrl);

    const uploadedBy =
      localStorage.getItem('subUsername') ||
      localStorage.getItem('username') ||
      'Meraki Ops';

    const today = new Date();
    const uploadDate = today.toISOString().slice(0, 10);
    const mimeType =
      this.selectedFile.type || this.guessMime(this.selectedFile.name);

    const record: ConsumptionExceptionRecord = {
      id: `local-${Date.now()}`,
      vesselName,
      imoNumber,
      fileName: this.selectedFile.name,
      uploadDate,
      uploadedBy,
      fileUrl: objectUrl,
      mimeType,
      status: 'Pending',
      fileSizeLabel: this.formatBytes(this.selectedFile.size),
      overspendMt: 0,
    };

    this.records = [record, ...this.records];
    this.pageIndex = 0;

    this.recordForm.reset({
      vesselName: '',
      imoNumber: '',
      category: '',
      priority: 'Medium',
    });
    this.clearSelectedFile(fileInput);
    this.openPreview(record, false);
    this.toastr.success('Vessel overspend record saved (demo).');
  }

  viewDocument(record: ConsumptionExceptionRecord): void {
    this.openPreview(record, true);
    this.actionMenuId = null;
  }

  downloadDocument(record: ConsumptionExceptionRecord): void {
    const anchor = document.createElement('a');
    anchor.href = record.fileUrl;
    anchor.download = record.fileName;
    anchor.target = '_blank';
    anchor.rel = 'noopener';
    anchor.click();
    this.actionMenuId = null;
  }

  deleteRecord(record: ConsumptionExceptionRecord): void {
    this.records = this.records.filter((r) => r.id !== record.id);
    if (this.previewRawUrl === record.fileUrl) {
      const next = this.records[0];
      if (next) {
        this.openPreview(next, false);
      } else {
        this.closePreview();
      }
    }
    if (this.pageIndex >= this.totalPages) {
      this.pageIndex = Math.max(0, this.totalPages - 1);
    }
    this.actionMenuId = null;
    this.toastr.success('Record removed (demo).');
  }

  toggleActionMenu(id: string, event: MouseEvent): void {
    event.stopPropagation();
    this.actionMenuId = this.actionMenuId === id ? null : id;
  }

  closeActionMenus(): void {
    this.actionMenuId = null;
  }

  closePreview(): void {
    this.previewOpen = false;
    this.previewFullscreen = false;
    this.previewSafeUrl = null;
    this.previewRawUrl = '';
    this.previewTitle = '';
    this.previewFileName = '';
    this.previewFileSize = '';
  }

  toggleFullscreen(): void {
    this.previewFullscreen = !this.previewFullscreen;
  }

  zoomIn(): void {
    this.zoomLevel = Math.min(200, this.zoomLevel + 10);
  }

  zoomOut(): void {
    this.zoomLevel = Math.max(50, this.zoomLevel - 10);
  }

  resetZoom(): void {
    this.zoomLevel = 100;
  }

  refreshData(): void {
    this.toastr.info('Records refreshed (demo).');
  }

  exportRecords(): void {
    if (!this.filteredRecords.length) {
      this.toastr.warning('No records to export.');
      return;
    }
    const header = [
      'Vessel Name',
      'IMO Number',
      'File Name',
      'Upload Date',
      'Uploaded By',
      'Status',
    ];
    const lines = this.filteredRecords.map((r) =>
      [
        r.vesselName,
        r.imoNumber,
        r.fileName,
        r.uploadDate,
        r.uploadedBy,
        r.status,
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(','),
    );
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'vessel-overspend-tracker.csv';
    anchor.click();
    URL.revokeObjectURL(url);
    this.toastr.success('CSV downloaded successfully.');
  }

  downloadFiltered(): void {
    this.exportRecords();
  }

  setSort(key: 'uploadDate' | 'vesselName' | 'imoNumber'): void {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = 'asc';
    }
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i);
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages) {
      return;
    }
    this.pageIndex = page;
  }

  onTableSearchChange(): void {
    this.pageIndex = 0;
  }

  fileIcon(fileName: string): string {
    const lower = fileName.toLowerCase();
    if (lower.endsWith('.pdf')) {
      return 'picture_as_pdf';
    }
    if (lower.endsWith('.doc') || lower.endsWith('.docx')) {
      return 'description';
    }
    return 'insert_drive_file';
  }

  statusClass(status: OverspendStatus): string {
    return `vot-badge--${status.toLowerCase()}`;
  }

  formatMetric(value: number, digits = 2): string {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  }

  sparklinePoints(series: string): string {
    const values = series.split(',').map((v) => Number(v));
    const max = Math.max(...values, 1);
    const min = Math.min(...values, 0);
    const range = max - min || 1;
    return values
      .map((v, i) => {
        const x = (i / (values.length - 1)) * 100;
        const y = 28 - ((v - min) / range) * 22;
        return `${x},${y}`;
      })
      .join(' ');
  }

  ngOnDestroy(): void {
    if (this.uploadTimer) {
      clearInterval(this.uploadTimer);
    }
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
    this.objectUrls = [];
  }

  private openPreview(
    record: ConsumptionExceptionRecord,
    forceOpen: boolean,
  ): void {
    this.previewTitle = `${record.vesselName} — ${record.fileName}`;
    this.previewFileName = record.fileName;
    this.previewFileSize = record.fileSizeLabel;
    this.previewIsPdf =
      record.mimeType.includes('pdf') ||
      record.fileName.toLowerCase().endsWith('.pdf');
    this.previewRawUrl = record.fileUrl;
    this.previewSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      record.fileUrl,
    );
    this.previewOpen = forceOpen ? true : this.previewOpen || true;
    this.zoomLevel = 100;
  }

  private handleIncomingFile(
    file: File | null,
    input?: HTMLInputElement,
  ): void {
    if (!file) {
      return;
    }

    if (!this.isAcceptedFile(file.name)) {
      this.toastr.error('Unsupported file type. Please upload PDF, DOC, or DOCX.');
      if (input) {
        input.value = '';
      }
      this.selectedFile = null;
      return;
    }

    if (file.size > ConsumptionExceptionsComponent.MAX_FILE_BYTES) {
      this.toastr.error('File exceeds the maximum size of 20MB.');
      if (input) {
        input.value = '';
      }
      this.selectedFile = null;
      return;
    }

    this.selectedFile = file;
    this.simulateUploadProgress();
  }

  private simulateUploadProgress(): void {
    if (this.uploadTimer) {
      clearInterval(this.uploadTimer);
    }
    this.isUploading = true;
    this.uploadProgress = 0;
    this.uploadTimer = setInterval(() => {
      this.uploadProgress = Math.min(100, this.uploadProgress + 12);
      if (this.uploadProgress >= 100) {
        this.isUploading = false;
        if (this.uploadTimer) {
          clearInterval(this.uploadTimer);
          this.uploadTimer = null;
        }
      }
    }, 80);
  }

  private isAcceptedFile(fileName: string): boolean {
    const lower = fileName.toLowerCase();
    return this.acceptedExtensions.some((ext) => lower.endsWith(ext));
  }

  private guessMime(fileName: string): string {
    const lower = fileName.toLowerCase();
    if (lower.endsWith('.pdf')) return 'application/pdf';
    if (lower.endsWith('.doc')) return 'application/msword';
    if (lower.endsWith('.docx')) {
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    }
    return 'application/octet-stream';
  }

  private formatBytes(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
